import { LoadingButton } from '@mui/lab'
import { Autocomplete, Button, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { getBreedFromSpecie } from '@providers/booking/helper'
import { useSessionContext } from '@providers/session/session_context'
import { i18WithParams as t } from '@shared/locale'
import { DatePicker } from 'components-ui/src/date_picker/date_picker'
import { isEmpty } from 'components-ui/src/shared/common'
import { MIN_DATE } from 'components-ui/src/shared/constants'
import { trimValues } from 'components-ui/src/shared/trim_values'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { SpeciesButtons } from 'components-ui/src/species_buttons/species_buttons'
import { TextInputNumber } from 'components-ui/src/textinput-number/textinput_number'
import { captureError } from 'service-api/src/shared/monitoring'
import {
  APILocationSpeciesClientDtoResponse,
  LocationPetProfileClientDto,
  LocationSpeciesClientDto,
  PetClientDto,
  SpeciesClientDto,
} from 'service-api/src/types/api'
import { TFormikValues, createPet, petSchema, setupInitialValues, updatePet } from './helper'

type PetFormProps = {
  pet?: LocationPetProfileClientDto
  onSave: (pet: PetClientDto) => void
  onCancel: () => void
  locationSpecies?: APILocationSpeciesClientDtoResponse | LocationSpeciesClientDto[]
  isLoadingSpeciesResponse?: boolean
  isEditing?: boolean
}

export const PetForm = ({
  pet,
  onSave,
  onCancel,
  isLoadingSpeciesResponse,
  locationSpecies,
  isEditing = false,
}: PetFormProps) => {
  const sessionCtx = useSessionContext()
  const bookingCtx = useBookingContext()
  const { accountName: locationName = '' } = useParams()
  const [selectedSpecies, setSelectedSpecies] = useState<LocationSpeciesClientDto>({
    id: '',
    name: '',
  })
  const [saving, setSaving] = useState(false)
  const lupId = sessionCtx.state.user?.lupId

  // Workaround till API returns order field https://goose-pet.atlassian.net/browse/GA-1288
  const species = useMemo(() => {
    let speciesData: LocationSpeciesClientDto[] = []
    if (locationSpecies && 'results' in locationSpecies) {
      speciesData = locationSpecies.results
    } else if (locationSpecies && Array.isArray(locationSpecies)) {
      speciesData = locationSpecies
    }

    return speciesData.reduce<LocationSpeciesClientDto[]>(
      (res: LocationSpeciesClientDto[], specie: LocationSpeciesClientDto) => {
        return specie.species?.name === 'dog' ? [specie, ...res] : [...res, specie]
      },
      [] as LocationSpeciesClientDto[]
    )
  }, [locationSpecies])

  const selectSpecies = useCallback(() => {
    if (species.length) {
      let speciesToSelect = species[0]
      if (pet) {
        const petSpecies = pet?.locationSpeciesId || pet?.locationSpecies?.id
        speciesToSelect = species.find(({ id }) => id === petSpecies) || species[0]
      }
      setSelectedSpecies(speciesToSelect)
    }
  }, [pet, species])

  useEffect(() => {
    selectSpecies()
  }, [selectSpecies, isEditing])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = useCallback((submitFn: Promise<any>, feedbackMsg: string) => {
    return submitFn
      .then((res) => {
        if (res?.id) {
          enqueueSnackbar(feedbackMsg, { variant: 'success' })
        } else {
          enqueueSnackbar(res?.response?.data?.error || t('global.label.unexpectedError'), { variant: 'error' })
        }
        return res
      })
      .catch((error) => {
        enqueueSnackbar(t('global.label.unexpectedError'), {
          variant: 'error',
        })
        captureError(error as Error)
      })
  }, [])

  const breed = getBreedFromSpecie(bookingCtx.state, pet?.locationSpeciesId || '', pet?.breed?.id || pet?.breedId || '')

  const formik = useFormik({
    initialValues: setupInitialValues(species[0], pet, breed),
    validationSchema: petSchema,
    onSubmit: (values) => {
      setSaving(true)
      // const locationSpecie = speciePlatformToLocation(bookingCtx.state, values.locationSpeciesId)
      const trimmedValues = trimValues(values) as TFormikValues
      if (trimmedValues) {
        handleSubmit(
          trimmedValues.id
            ? updatePet(trimmedValues, locationName, lupId || '')
            : createPet(trimmedValues, locationName, lupId || ''),
          trimmedValues.id ? t('petForm.edit.success') : t('petForm.create.success')
        )
          .then((res) => res.id && onSave(res))
          .finally(() => setSaving(false))
      }
    },
  })

  const baseFormikProps = useCallback(
    (fieldName: keyof TFormikValues) => ({
      id: fieldName,
      name: fieldName,
      onChange: formik.handleChange,
      value: formik.values[fieldName],
      error: formik.touched[fieldName] && Boolean(formik.errors[fieldName]),
      helperText: formik.touched[fieldName] && formik.errors[fieldName] ? t('global.label.required') : null,
    }),
    [formik]
  )

  const onSelectSpecies = useCallback(
    (newSpecies: SpeciesClientDto) => {
      if (newSpecies.id !== selectedSpecies.id) {
        const petData = newSpecies.id === pet?.breed?.species?.id ? pet : {}
        formik.resetForm({
          values: setupInitialValues(newSpecies, petData),
        })
        setSelectedSpecies(newSpecies)
      }
    },
    [selectedSpecies, formik, pet]
  )

  if (!species.length) {
    return null
  }

  return (
    <Paper sx={{ px: 2, pt: 2, pb: 3, width: '100%' }}>
      {pet ? (
        <Typography variant="h5">{t('petForm.title.edit')}</Typography>
      ) : (
        <Typography variant="h5">{t('petForm.title.add')}</Typography>
      )}

      <Spacer height={4} />
      <Grid container spacing={1}>
        {!isEditing && (
          <Grid item xs={12}>
            <SpeciesButtons
              species={species}
              selected={selectedSpecies.id || ''}
              onSelect={(val) => onSelectSpecies(val as SpeciesClientDto)}
              size="small"
            />
            <Spacer height={2} />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            label={t('global.label.petFirstName')}
            size="small"
            fullWidth
            {...baseFormikProps('displayName')}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            id="breed"
            includeInputInList
            filterSelectedOptions
            size="small"
            clearIcon={null}
            loading={isLoadingSpeciesResponse}
            loadingText={t('global.label.loading')}
            options={bookingCtx.state.config?.breedsBySpecie?.[selectedSpecies.id!] ?? []}
            getOptionLabel={(option) => option.displayName || option.name || ''}
            value={isEmpty(formik.values.breed) ? null : formik.values.breed}
            onChange={(_, value) =>
              formik.setFieldValue('breed', value ? { ...value, species: selectedSpecies } : null)
            }
            onBlur={formik.handleBlur}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                id="breed"
                name="breed"
                label={t('global.label.breed')}
                fullWidth
                error={formik.touched.breed && Boolean(formik.errors.breed)}
                helperText={baseFormikProps('breed').helperText}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInputNumber
            label={t('global.label.weight')}
            size="small"
            fullWidth
            {...baseFormikProps('weight')}
            value={formik.values.weight}
          />
        </Grid>
        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={formik.values.birthdate}
              onChange={(date) => formik.setFieldValue('birthdate', date)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="birthdate"
                  name="birthdate"
                  label={t('global.label.dateOfBirth_optional')}
                  size="small"
                  fullWidth
                  error={
                    (formik.touched.birthdate && Boolean(formik.errors.birthdate)) ||
                    (!!formik.values.birthdate && new Date(formik.values.birthdate) < MIN_DATE)
                  }
                  helperText={formik.touched.birthdate && formik.errors.birthdate}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField label={t('global.label.sex_optional')} size="small" select fullWidth {...baseFormikProps('sex')}>
            <MenuItem value="MALE">{t('global.label.male')}</MenuItem>
            <MenuItem value="FEMALE">{t('global.label.female')}</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('petForm.isAltered', {
              petSpecies: selectedSpecies.name!,
            })}
            size="small"
            select
            fullWidth
            {...baseFormikProps('altered')}
          >
            <MenuItem value="yes">{t('global.label.yes')}</MenuItem>
            <MenuItem value="no">{t('global.label.no')}</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}></Grid>
      </Grid>

      <Spacer height={2} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={2}>
          <LoadingButton
            loading={saving}
            variant="contained"
            onClick={() => {
              if (formik.dirty) {
                formik.handleSubmit()
              }
            }}
          >
            {t('global.label.save')}
          </LoadingButton>
          <Button onClick={() => onCancel()}>{t('global.label.cancel')}</Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
